import { CategoryItem, OrderItem, PurchaseOffers, SelectedArticle } from "../interfaces/Catalogue";
import { CodePromoSuggestion, LoyalityGroup, PanierDiscount, OfferGroup, ArticleRewardRule, LoyaltyArticle } from "../interfaces/loyality.interface";
import { findPriceByTypeOrder } from "./priceUtils";


export const generateLoyaltyDiscountMessage = (panierDiscount: PanierDiscount): string => {
  const remiseMessage =
    panierDiscount.cartRewardDiscountType === 0
      ? `${panierDiscount.cartRewardDiscountValue}%`
      : `${panierDiscount.cartRewardDiscountValue?.toFixed(2)} €`;

  return `Voulez-vous bénéficier d’une réduction de ${remiseMessage} contre ${panierDiscount.cartRewardTotalPtRequired} points de fidélité ?`;
};

export const calculDiscountAmount = (panierDiscount: PanierDiscount, amount: number): number => {
  if (panierDiscount.cartRewardDiscountType === 0) {
    // Percentage-based discount
    return (amount * panierDiscount.cartRewardDiscountValue) / 100;
  } else {
    // Fixed amount discount
    return panierDiscount.cartRewardDiscountValue;
  }
};

export const calculDiscountAmountForLoyaltyGroup = (loyalityGroup: LoyalityGroup, amount: number): number => {
  if (loyalityGroup.isFormule) {
    if (amount >= loyalityGroup.partSalariale && amount <= loyalityGroup.seuilMax) {
      const payedAmount = Math.max(loyalityGroup.partSalariale, 0);
      return Math.max(amount - payedAmount, 0);
    } else {
      const amountAboveSeuilMax = amount > loyalityGroup.seuilMax ? amount - loyalityGroup.seuilMax : 0;
      const payedAmount = Math.max(loyalityGroup.partSalariale + amountAboveSeuilMax, 0);
      return Math.max(amount - payedAmount, 0);
    }
  } else {
    return (amount * loyalityGroup.taux) / 100;
  }
};


export const calculateTotalLoyaltyPtsConsumedForMenuOrArticle = (category: CategoryItem): number => {
  let totalLoyaltyPtsConsumed: number = 0
  if (category?.order) {

    category?.order?.map((orderItem) => {
      orderItem.selectedArticles.map((selectedArticle) => {
        totalLoyaltyPtsConsumed += selectedArticle.selectedMenuItem.isRemiseActive ? selectedArticle.selectedMenuItem.rewardTotalPtRequired * (selectedArticle.selectedMenuItem.quantity ?? 0) : 0
      })
    })
    return totalLoyaltyPtsConsumed * (category.quantity ?? 0)
  } else {
    return category.isRemiseActive ? category.rewardTotalPtRequired * (category.quantity ?? 0) : 0
  }

}

export const calculateTotalLoyaltyPtsConsumedForOrder = (categories: CategoryItem[]): number => {
  let totalLoyaltyPtsConsumed: number = 0

  categories?.map(category => {
    totalLoyaltyPtsConsumed += calculateTotalLoyaltyPtsConsumedForMenuOrArticle(category)
  })

  return totalLoyaltyPtsConsumed

}

export const getRemiseMessage = (selectedMenuItem: {
  rewardDiscountType: number;
  rewardDiscountValue: number;
  rewardTotalPtRequired: number;
}): string => {
  const remiseMessage =
    selectedMenuItem.rewardDiscountType === 0
      ? `${selectedMenuItem.rewardDiscountValue}%`
      : `${selectedMenuItem.rewardDiscountValue?.toFixed(2)} €`;

  return ` ${remiseMessage} offerts pour ${selectedMenuItem.rewardTotalPtRequired} points`;
};


export const getRewardDiscountDisplay = (selectedMenuItem: {
  rewardDiscountType: number;
  rewardDiscountValue: number;
  rewardTotalPtRequired: number;
}): string => {
  if (selectedMenuItem.rewardDiscountType === 0) {
    return `${selectedMenuItem.rewardDiscountValue}%`;
  } else {
    return `${selectedMenuItem.rewardDiscountValue?.toFixed(2)} €`;
  }
};

export const calculatePromoCodeDiscount = (price: number, codePromoSuggestion: CodePromoSuggestion): number => {
  return codePromoSuggestion.discountType === 0
    ? (price * codePromoSuggestion.discountValue) / 100 // Percentage discount
    : codePromoSuggestion.discountValue; // Fixed amount discount
};


export const getPurchaseOfferMessage = (purchaseOffer: { designation: string; nbPurchased: number; nbOffered: number; }): string => {
  return `${purchaseOffer.nbPurchased} achaté(s) = ${purchaseOffer.nbOffered} offert(s)`;
}

export const isPurchaseOfferActive = (purchaseOffer: PurchaseOffers): boolean => {
  if (!purchaseOffer) return false;

  const now = new Date();
  const { beginDate, endDate, nbOffered, nbPurchased } = purchaseOffer;

  const isInactive =
    (beginDate && new Date(beginDate) > now) ||
    (endDate && new Date(endDate) < now);
  return !isInactive && nbPurchased > 0 && nbOffered > 0;

}

export const tagFreeItems = (
  order: OrderItem[],
  orderType: number | null
): OrderItem[] => {
  if (!order?.length) return [];

  const withOffers: SelectedArticle[] = [];
  const withoutOffers: SelectedArticle[] = [];

  // Split into with/without offers
  order.forEach(({ selectedArticles = [] }) => {
    selectedArticles.forEach((sa) =>
      sa.selectedMenuItem?.purchaseOffers ? withOffers.push(sa) : withoutOffers.push(sa)
    );
  });

  // Sort by descending unit price
  withOffers.sort((a, b) => {
    const priceA = findPriceByTypeOrder(a.selectedMenuItem?.typeOrderPrices, orderType) ?? 0;
    const priceB = findPriceByTypeOrder(b.selectedMenuItem?.typeOrderPrices, orderType) ?? 0;
    return priceB - priceA;
  });

  const groupedOffers = groupArticlesByOffer(withOffers);
  const markedFreeArticles = markFreeArticles(groupedOffers);
  // console.log("🚀 ~ markedFreeArticles:", markedFreeArticles)

  const updatedOrder = order.map((orderItem) => ({
    ...orderItem,
    selectedArticles: orderItem.selectedArticles.map((article) => {
      const isFree = markedFreeArticles.some((group) =>
        group.selectedArticles.some((selectedArticle) => {
          const { selectedMenuItem } = selectedArticle;
          return (
            selectedMenuItem?.id === article.selectedMenuItem?.id &&
            selectedMenuItem?.purchaseOffers?.id === group.purchaseOffers.id &&
            selectedMenuItem.isFree
          );
        }));

      return {
        ...article,
        selectedMenuItem: {
          ...article.selectedMenuItem,
          isFree: isFree,
        },

      };
    }),
  }));
  // console.log("🚀 ~ updatedOrder ~ updatedOrder:", updatedOrder)

  return updatedOrder;
}

const markFreeArticles = (
  groups: OfferGroup[]
): OfferGroup[] => {

  groups.forEach((group) => {
    const { nbPurchased, nbOffered } = group.purchaseOffers;
    const bundleSize = nbPurchased + nbOffered;

    group.selectedArticles.forEach((article, idx) => {
      article.selectedMenuItem.isFree = idx % bundleSize >= nbPurchased;
    });
  });

  return groups;
};



export const groupArticlesByOffer = (
  selectedArticle: SelectedArticle[]
): OfferGroup[] => {
  const groupedOffers = new Map<string, OfferGroup>();

  selectedArticle.forEach((article) => {
    const offer = article.selectedMenuItem?.purchaseOffers;
    if (!offer) return;

    const offerKey = JSON.stringify(offer);

    if (!groupedOffers.has(offerKey)) {
      groupedOffers.set(offerKey, {
        purchaseOffers: offer,
        selectedArticles: [],
      });
    }

    groupedOffers.get(offerKey)!.selectedArticles.push(article);
  });

  const groupedOffersArray = Array.from(groupedOffers.values());
  // console.log("🚀 ~ groupedOffersArray:", groupedOffersArray);

  return groupedOffersArray;
};


  export const transformToLoyaltyItems = (
    articles: ArticleRewardRule[],
    userLoyaltyPoints: number,
  ): LoyaltyArticle[] => {
    return articles.map(article => {
      const discountPercent = article.rewardDiscountValue;
      const isLocked = userLoyaltyPoints < article.rewardTotalPtRequired;

      return {
        id: article.id.toString(),
        name: article.name,
        discountPercent: discountPercent,
        image: article.imageUrl ?? undefined,
        pointsRequired: article.rewardTotalPtRequired,
        isLocked: isLocked,
        missingPoints: isLocked
          ? article.rewardTotalPtRequired - userLoyaltyPoints
          : 0,
      };
    });
  };